<template>
  <div v-if="ads.length" class="gs-section">
    <div class="gs-section__toolbar">
      <p class="gs-section__count">{{ countLabel }}</p>
    </div>

    <div class="gs-asset-grid gs-asset-grid--wide">
      <asset-card v-for="ad in ads" :key="previewKey(ad)" collapsible :title="ad.name">
        <template slot="toolbar">
          <div class="gs-segmented" role="group" aria-label="Body view">
            <button
              v-for="view in views"
              :key="view.value"
              type="button"
              class="gs-segmented__item"
              :class="{ 'is-current': viewFor(ad) === view.value }"
              :aria-pressed="viewFor(ad) === view.value ? 'true' : 'false'"
              :data-view="view.value"
              @click="setView(ad, view.value)"
            >
              {{ view.title }}
            </button>
          </div>
        </template>

        <template slot="preview">
          <!-- The vendor's own ad markup, as trusted here as in the legacy view. -->
          <div
            v-if="viewFor(ad) === 'preview'"
            class="gs-asset-html gs-ad-preview"
            :class="'gs-preview-' + previewKey(ad)"
            v-html="previewHtml[previewKey(ad)]"
          ></div>
          <pre v-else class="gs-asset-code">{{ ad.body }}</pre>
        </template>

        <template slot="actions">
          <copy-button
            :text="ad.body"
            :label="`${$t('frontend_copy')} ${$t('frontend_affiliate_promo_embed_code')}`"
            :title="$t('frontend_resource_copy_embed')"
          />
        </template>
      </asset-card>
    </div>
  </div>

  <div v-else class="gs-empty">
    <span class="gs-empty__icon"><gs-icon name="check" size="22" /></span>
    <p class="gs-empty__title">{{ $t('frontend_resource_no_thankyou') }}</p>
    <p class="gs-empty__text">
      Ads you can drop onto your own thank-you page show up here.
    </p>
  </div>
</template>

<script>
import AssetCard from '@/components/promotools/AssetCard.vue';
import CopyButton from '@/components/promotools/CopyButton.vue';
import { previewKeyMap } from '@/components/promotools/previewKeys';
import { sanitiseHtml } from '@/components/promotools/sanitiseHtml';

export default {
  components: {
    AssetCard,
    CopyButton,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Null-prototype, so an asset whose key is `__proto__` cannot reach
      // Object.prototype instead of its own entry.
      adViews: Object.create(null),
    };
  },
  computed: {
    // "View HTML" is the vendor-side label this body has always carried; only
    // "Preview" is new, so only it is English.
    views() {
      return [
        { title: 'Preview', value: 'preview' },
        { title: this.$t('frontend_resources_view_html'), value: 'html' },
      ];
    },
    ads() {
      return (this.funnelResource && this.funnelResource.thankyou_page_ads
        ? this.funnelResource.thankyou_page_ads.filter(ad => ad.enabled)
        : []);
    },
    countLabel() {
      const count = this.ads.length;
      return `${count} ${count === 1 ? 'ad' : 'ads'}`;
    },
    previewKeys() {
      return previewKeyMap(this.ads);
    },
    // Vendor markup is previewed, never executed. Sanitising is a parse, so
    // it happens once per asset here rather than on every re-render; the map
    // is rebuilt whenever the funnel resource changes. The ad's own stylesheet
    // is rewritten to match only inside this card's preview well.
    previewHtml() {
      return this.ads.reduce((map, ad) => {
        const key = this.previewKeys.get(ad);
        map[key] = sanitiseHtml(ad.body, { scope: `gs-preview-${key}` });
        return map;
      }, Object.create(null));
    },
  },
  methods: {
    previewKey(ad) {
      return this.previewKeys.get(ad) || '';
    },
    viewFor(ad) {
      return this.adViews[this.previewKey(ad)] || 'preview';
    },
    setView(ad, view) {
      this.$set(this.adViews, this.previewKey(ad), view);
    },
  },
};
</script>

<style scoped>
/* Page ads are built to sit centred on a full-width page. */
.gs-ad-preview {
  text-align: center;
}
</style>
